import {
  LEAVE_ABSENCE_KIND_LABELS,
  LeaveAbsenceColor,
  leaveAbsenceKindSchema,
  type LeaveAbsenceKindCode,
  type LeaveAbsenceType,
} from '@gb-ticket/shared-types';
import { type FormEvent, type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select as UiSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { LeaveDayCell } from '@/products/leave/features/leave-day-cell';
import {
  LEAVE_TYPE_COLOR_CLASS,
  LEAVE_TYPE_COLOR_LABEL,
  LEAVE_TYPE_COLOR_OPTIONS,
} from '@/products/leave/features/leave-type-colors';
import {
  useCreateLeaveAbsenceType,
  useDeleteLeaveAbsenceType,
  useLeaveAbsenceTypeCatalog,
  useUpdateLeaveAbsenceType,
} from '@/products/leave/features/use-leave-admin';

type AbsenceColor = (typeof LeaveAbsenceColor)[keyof typeof LeaveAbsenceColor];

type TypeDraft = {
  labelDe: string;
  color: AbsenceColor;
  mark: string;
  defaultDays: string;
  maxDaysPerRequest: string;
  sortOrder: string;
  requiresApproval: boolean;
  countsTowardCapacity: boolean;
  visibleToColleagues: boolean;
  isActive: boolean;
};

type CreateDraft = TypeDraft & {
  code: string;
  kind: LeaveAbsenceKindCode;
};

const EMPTY_CREATE: CreateDraft = {
  code: '',
  labelDe: '',
  kind: 'vacation',
  color: LeaveAbsenceColor.SKY,
  mark: '',
  defaultDays: '',
  maxDaysPerRequest: '',
  sortOrder: '200',
  requiresApproval: true,
  countsTowardCapacity: true,
  visibleToColleagues: true,
  isActive: true,
};

export function LeaveAdminAbsenceTypesPage() {
  usePageTitle('Abwesenheitsarten');
  const catalog = useLeaveAbsenceTypeCatalog();
  const save = useUpdateLeaveAbsenceType();
  const create = useCreateLeaveAbsenceType();
  const remove = useDeleteLeaveAbsenceType();
  const [drafts, setDrafts] = useState<Record<string, TypeDraft>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createDraft, setCreateDraft] = useState<CreateDraft>(EMPTY_CREATE);
  const [createError, setCreateError] = useState<string | null>(null);

  function draftFor(item: LeaveAbsenceType): TypeDraft {
    return (
      drafts[item.id] ?? {
        labelDe: item.labelDe,
        color: item.color,
        mark: item.mark,
        defaultDays: item.defaultDays == null ? '' : String(item.defaultDays),
        maxDaysPerRequest: item.maxDaysPerRequest == null ? '' : String(item.maxDaysPerRequest),
        sortOrder: String(item.sortOrder),
        requiresApproval: item.requiresApproval,
        countsTowardCapacity: item.countsTowardCapacity,
        visibleToColleagues: item.visibleToColleagues,
        isActive: item.isActive,
      }
    );
  }

  function patch(id: string, item: LeaveAbsenceType, patchValue: Partial<TypeDraft>) {
    setDrafts((current) => {
      const base = current[id] ?? {
        labelDe: item.labelDe,
        color: item.color,
        mark: item.mark,
        defaultDays: item.defaultDays == null ? '' : String(item.defaultDays),
        maxDaysPerRequest: item.maxDaysPerRequest == null ? '' : String(item.maxDaysPerRequest),
        sortOrder: String(item.sortOrder),
        requiresApproval: item.requiresApproval,
        countsTowardCapacity: item.countsTowardCapacity,
        visibleToColleagues: item.visibleToColleagues,
        isActive: item.isActive,
      };
      return { ...current, [id]: { ...base, ...patchValue } };
    });
  }

  async function onSave(item: LeaveAbsenceType, event: FormEvent) {
    event.preventDefault();
    const values = draftFor(item);
    const parsed = parseDraft(values);
    if (typeof parsed === 'string') {
      setErrors((current) => ({ ...current, [item.id]: parsed }));
      return;
    }
    setErrors((current) => {
      const next = { ...current };
      delete next[item.id];
      return next;
    });
    try {
      await save.mutateAsync({ id: item.id, body: parsed });
      setDrafts((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      toast.success('Abwesenheitsart gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Speichern ist fehlgeschlagen.',
      );
    }
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    const parsed = parseDraft(createDraft);
    if (typeof parsed === 'string') {
      setCreateError(parsed);
      return;
    }
    if (!/^[a-z][a-z0-9_]{0,31}$/.test(createDraft.code.trim())) {
      setCreateError('Der Code beginnt mit einem Buchstaben und nutzt a–z, 0–9 oder _.');
      return;
    }
    setCreateError(null);
    try {
      await create.mutateAsync({
        ...parsed,
        code: createDraft.code.trim(),
        kind: createDraft.kind,
      });
      setCreateDraft(EMPTY_CREATE);
      toast.success('Abwesenheitsart angelegt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Anlegen ist fehlgeschlagen.');
    }
  }

  async function onDelete(item: LeaveAbsenceType) {
    try {
      await remove.mutateAsync(item.id);
      toast.success('Abwesenheitsart gelöscht.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen ist fehlgeschlagen.');
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Abwesenheitsarten"
        description="Bezeichnungen, Farben und Regeln. Die Berechnung bleibt an der Art hängen."
      />
      {catalog.isPending ? (
        <p className="text-sm text-muted-foreground">Katalog wird geladen …</p>
      ) : null}
      <form
        className={`${leavePanelClassName} space-y-4`}
        onSubmit={(event) => void onCreate(event)}
      >
        <p className="font-medium">Neue Art</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Code" htmlFor="new-code">
            <Input
              id="new-code"
              value={createDraft.code}
              onChange={(event) =>
                setCreateDraft((current) => ({ ...current, code: event.target.value }))
              }
            />
          </Field>
          <Field label="Bezeichnung" htmlFor="new-label">
            <Input
              id="new-label"
              value={createDraft.labelDe}
              onChange={(event) =>
                setCreateDraft((current) => ({ ...current, labelDe: event.target.value }))
              }
            />
          </Field>
          <Field label="Rechenregel" htmlFor="new-kind">
            <Select
              id="new-kind"
              value={createDraft.kind}
              onChange={(value) =>
                setCreateDraft((current) => ({
                  ...current,
                  kind: leaveAbsenceKindSchema.parse(value),
                }))
              }
              options={Object.entries(LEAVE_ABSENCE_KIND_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </Field>
          <ColorField
            id="new-color"
            value={createDraft.color}
            onChange={(color) => setCreateDraft((current) => ({ ...current, color }))}
          />
          <MarkField
            id="new-mark"
            value={createDraft.mark}
            color={createDraft.color}
            labelDe={createDraft.labelDe || 'Neue Art'}
            onChange={(mark) => setCreateDraft((current) => ({ ...current, mark }))}
          />
        </div>
        <FlagFields
          values={createDraft}
          onChange={(patchValue) => setCreateDraft((current) => ({ ...current, ...patchValue }))}
        />
        {createError ? <p className="text-sm text-destructive">{createError}</p> : null}
        <Button type="submit" disabled={create.isPending}>
          Anlegen
        </Button>
      </form>
      <div className="space-y-4">
        {catalog.data?.items.map((item) => {
          const values = draftFor(item);
          return (
            <form
              key={item.id}
              className={`${leavePanelClassName} space-y-3`}
              onSubmit={(event) => void onSave(item, event)}
            >
              <div className="flex flex-wrap items-center gap-2">
                <LeaveDayCell
                  size="chip"
                  color={values.color}
                  mark={/^[A-Z0-9]{1,2}$/.test(values.mark) ? values.mark : item.mark}
                  status="approved"
                  labelDe={values.labelDe || item.labelDe}
                />
                <p className="font-medium">{item.labelDe}</p>
                <p className="text-sm text-muted-foreground">
                  {item.code} · {LEAVE_ABSENCE_KIND_LABELS[item.kind]}
                  {item.system ? ' · Systemart' : ''}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Bezeichnung" htmlFor={`label-${item.id}`}>
                  <Input
                    id={`label-${item.id}`}
                    value={values.labelDe}
                    onChange={(event) => patch(item.id, item, { labelDe: event.target.value })}
                  />
                </Field>
                <ColorField
                  id={`color-${item.id}`}
                  value={values.color}
                  onChange={(color) => patch(item.id, item, { color })}
                />
                <MarkField
                  id={`mark-${item.id}`}
                  value={values.mark}
                  color={values.color}
                  labelDe={values.labelDe || item.labelDe}
                  onChange={(mark) => patch(item.id, item, { mark })}
                />
                <Field label="Standardtage" htmlFor={`days-${item.id}`}>
                  <Input
                    id={`days-${item.id}`}
                    value={values.defaultDays}
                    onChange={(event) => patch(item.id, item, { defaultDays: event.target.value })}
                  />
                </Field>
                <Field label="Höchstens Tage" htmlFor={`max-${item.id}`}>
                  <Input
                    id={`max-${item.id}`}
                    value={values.maxDaysPerRequest}
                    onChange={(event) =>
                      patch(item.id, item, { maxDaysPerRequest: event.target.value })
                    }
                  />
                </Field>
                <Field label="Reihenfolge" htmlFor={`sort-${item.id}`}>
                  <Input
                    id={`sort-${item.id}`}
                    value={values.sortOrder}
                    onChange={(event) => patch(item.id, item, { sortOrder: event.target.value })}
                  />
                </Field>
              </div>
              <FlagFields
                values={values}
                onChange={(patchValue) => patch(item.id, item, patchValue)}
              />
              {errors[item.id] ? (
                <p className="text-sm text-destructive">{errors[item.id]}</p>
              ) : null}
              <div className="flex gap-2">
                <Button type="submit" disabled={save.isPending}>
                  Speichern
                </Button>
                {item.system ? null : (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={remove.isPending}
                    onClick={() => void onDelete(item)}
                  >
                    Löschen
                  </Button>
                )}
              </div>
            </form>
          );
        })}
      </div>
    </div>
  );
}

function parseDraft(values: TypeDraft):
  | (Omit<TypeDraft, 'defaultDays' | 'maxDaysPerRequest' | 'sortOrder' | 'mark'> & {
      mark: string;
      defaultDays: number | null;
      maxDaysPerRequest: number | null;
      sortOrder: number;
    })
  | string {
  if (!values.labelDe.trim()) {
    return 'Bitte eine Bezeichnung eingeben.';
  }
  const mark = values.mark.trim().toUpperCase();
  if (!/^[A-Z0-9]{1,2}$/.test(mark)) {
    return 'Das Kurzzeichen ist ein oder zwei Zeichen A–Z oder 0–9.';
  }
  const defaultDays = parseOptionalDays(values.defaultDays);
  const maxDaysPerRequest = parseOptionalDays(values.maxDaysPerRequest);
  const sortOrder = Number(values.sortOrder);
  if (defaultDays === 'invalid' || maxDaysPerRequest === 'invalid') {
    return 'Tage sind eine Zahl zwischen 0 und 60, oder leer.';
  }
  if (!Number.isInteger(sortOrder) || sortOrder < 0 || sortOrder > 999) {
    return 'Die Reihenfolge ist eine ganze Zahl von 0 bis 999.';
  }
  return {
    labelDe: values.labelDe.trim(),
    color: values.color,
    mark,
    defaultDays,
    maxDaysPerRequest,
    sortOrder,
    requiresApproval: values.requiresApproval,
    countsTowardCapacity: values.countsTowardCapacity,
    visibleToColleagues: values.visibleToColleagues,
    isActive: values.isActive,
  };
}

function parseOptionalDays(value: string): number | null | 'invalid' {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 60) {
    return 'invalid';
  }
  return parsed;
}

function FlagFields({
  values,
  onChange,
}: {
  values: Pick<
    TypeDraft,
    'requiresApproval' | 'countsTowardCapacity' | 'visibleToColleagues' | 'isActive'
  >;
  onChange: (
    patch: Partial<
      Pick<
        TypeDraft,
        'requiresApproval' | 'countsTowardCapacity' | 'visibleToColleagues' | 'isActive'
      >
    >,
  ) => void;
}) {
  return (
    <div className="flex flex-wrap gap-4 text-sm">
      <Check
        label="Genehmigung nötig"
        checked={values.requiresApproval}
        onChange={(requiresApproval) => onChange({ requiresApproval })}
      />
      <Check
        label="Zählt für die Besetzung"
        checked={values.countsTowardCapacity}
        onChange={(countsTowardCapacity) => onChange({ countsTowardCapacity })}
      />
      <Check
        label="Kolleginnen sehen die Art"
        checked={values.visibleToColleagues}
        onChange={(visibleToColleagues) => onChange({ visibleToColleagues })}
      />
      <Check
        label="Aktiv"
        checked={values.isActive}
        onChange={(isActive) => onChange({ isActive })}
      />
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <Checkbox checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      {label}
    </label>
  );
}

function ColorField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: AbsenceColor;
  onChange: (color: AbsenceColor) => void;
}) {
  return (
    <Field label="Farbe" htmlFor={id}>
      <span className="flex items-center gap-2">
        <span className={`size-3 shrink-0 rounded-full ${LEAVE_TYPE_COLOR_CLASS[value]}`} />
        <Select
          id={id}
          value={value}
          onChange={(next) => onChange(next as AbsenceColor)}
          options={LEAVE_TYPE_COLOR_OPTIONS.map((color) => ({
            value: color,
            label: LEAVE_TYPE_COLOR_LABEL[color],
          }))}
        />
      </span>
    </Field>
  );
}

function MarkField({
  id,
  value,
  color,
  labelDe,
  onChange,
}: {
  id: string;
  value: string;
  color: AbsenceColor;
  labelDe: string;
  onChange: (mark: string) => void;
}) {
  const preview = /^[A-Z0-9]{1,2}$/.test(value.trim().toUpperCase())
    ? value.trim().toUpperCase()
    : null;
  return (
    <Field label="Kurzzeichen" htmlFor={id}>
      <span className="flex items-center gap-2">
        <Input
          id={id}
          className="w-20 uppercase"
          maxLength={2}
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          placeholder="U"
        />
        <LeaveDayCell
          size="chip"
          color={color}
          mark={preview}
          status="approved"
          labelDe={labelDe}
        />
        <LeaveDayCell
          size="chip"
          color={color}
          mark={preview}
          status="submitted"
          labelDe={labelDe}
        />
      </span>
    </Field>
  );
}

function Select({
  id,
  value,
  onChange,
  options,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <UiSelect value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </UiSelect>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
